import MarkdownToJSX from "markdown-to-jsx";
import clsx from "clsx";

import SEO from "../common/seo";
import Image from "../common/Image";
import NextImage from "next/image";
import { WhoppahFullSize } from "@/assets/icons/Logo";

import OtherBlogs from "./OtherBlogs";
import Breadcrumb from "../common/Breadcrumb";
import BlogProducts from "./BlogProducts";
import { FeedHead } from "./feed-head";
import { cn } from "@/common/utils/css.utils";

const Markdown = ({ children, className }) => {
  if (!children) return null;
  return (
    <MarkdownToJSX
      className={clsx("prose max-w-none", className)}
      options={{
        overrides: {
          a: {
            component: "a",
            props: {
              className: "text-primary-500 hover:underline",
            },
          },
          p: {
            component: "p",
            props: {
              className: "text-[14px] md:text-[18px]",
            },
          },
          h2: {
            component: "h2",
            props: {
              className: "font-poppins text-[30px] md:text-[36px] mt-2 mb-1",
            },
          },
          h3: {
            component: "h3",
            props: {
              className: "font-poppins text-[24px] md:text-[30px] mt-2 mb-1",
            },
          },
        },
      }}
    >
      {children}
    </MarkdownToJSX>
  );
};

const ImageWithTextBlock = ({ block }) => {
  return (
    <section className="grid grid-cols-1 gap-x-[24px] gap-y-[24px] md:grid-cols-2">
      <div
        className={clsx(
          "relative min-h-[280px] w-full overflow-hidden rounded-[10px]",
          block.imagePosition === "right" && "order-1"
        )}
      >
        <Image
          layout="fill"
          objectFit="cover"
          src={block.image?.data?.attributes?.url}
          alt={block.image?.data?.attributes?.alternativeText || ""}
        />
      </div>
      <Markdown>{block.content}</Markdown>
    </section>
  );
};

const FigureWithCaption = ({
  width,
  height,
  url,
  alternativeText,
  caption,
}) => {
  return (
    <figure className="flex flex-col justify-center">
      <NextImage
        className={cn("h-[calc(100%-1rem)] object-contain")}
        width={width}
        height={height}
        src={url}
        alt={alternativeText || ""}
      />
      {caption && (
        <figcaption className="text-center text-sm text-gray-500">
          {caption}
        </figcaption>
      )}
    </figure>
  );
};

const TwoImagesBlock = ({ block, author }) => {
  const isDM = author.data.attributes.name === "Design Market";

  if (isDM) {
    return (
      <section className="grid grid-cols-1 gap-x-[24px] gap-y-[24px] md:grid-cols-2">
        <div className={cn("relative w-full overflow-hidden rounded-[10px]")}>
          <div className="mx-auto flex max-h-[40rem] w-[32rem] justify-center">
            <FigureWithCaption {...block.leftImage?.data?.attributes} />
          </div>
        </div>
        <div className={cn("relative w-full overflow-hidden rounded-[10px]")}>
          <div className="mx-auto flex max-h-[40rem] w-[32rem] justify-center">
            <FigureWithCaption {...block.rightImage?.data?.attributes} />
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="grid grid-cols-1 gap-x-[24px] gap-y-[24px] md:grid-cols-2">
      <div
        className={clsx(
          "relative h-[280px] w-full overflow-hidden rounded-[10px] md:h-full md:min-h-[500px]"
        )}
      >
        <Image
          layout="fill"
          objectFit="cover"
          width={block.leftImage?.data?.attributes?.width}
          height={block.leftImage?.data?.attributes?.height}
          src={block.leftImage?.data?.attributes?.url}
          alt={block.leftImage?.data?.attributes?.alternativeText || ""}
        />
      </div>
      <div
        className={clsx(
          "relative h-[280px] w-full overflow-hidden rounded-[10px] md:h-full md:min-h-[500px]"
        )}
      >
        <Image
          layout="fill"
          objectFit="cover"
          width={block.rightImage?.data?.attributes?.width}
          height={block.rightImage?.data?.attributes?.height}
          src={block.rightImage?.data?.attributes?.url}
          alt={block.rightImage?.data?.attributes?.alternativeText || ""}
        />
      </div>
    </section>
  );
};

const ImageBlock = ({ block, author }) => {
  const isDM = author.data.attributes.name === "Design Market";

  if (isDM) {
    return (
      <section className="grid grid-cols-1 gap-x-[24px] gap-y-[24px]">
        <div className="mx-auto flex w-[48rem] flex-col justify-center">
          <FigureWithCaption {...block.image?.data?.attributes} />
        </div>
      </section>
    );
  }

  return (
    <section className="grid grid-cols-1 gap-x-[24px] gap-y-[24px]">
      <div
        className={clsx(
          "relative h-[280px] w-full overflow-hidden rounded-[10px] md:h-full md:min-h-[500px]"
        )}
      >
        <Image
          layout="fill"
          objectFit="cover"
          src={block.image?.data?.attributes.url}
          alt={block.image?.data?.attributes.alternativeText || ""}
        />
      </div>
    </section>
  );
};

const TextBlock = ({ block }) => {
  return (
    <section className="grid grid-cols-1 gap-x-[24px] gap-y-[24px]">
      <Markdown>{block.content}</Markdown>
    </section>
  );
};

const Block = ({ block, author }) => {
  const componentMap = {
    ComponentPagesBlock: ImageWithTextBlock,
    ComponentPagesText: TextBlock,
    ComponentPagesTwoImages: TwoImagesBlock,
    ComponentPagesImage: ImageBlock,
  };

  if (block.__typename in componentMap) {
    const Component = componentMap[block.__typename];
    return <Component block={block} author={author} />;
  }
  console.warn("The block type is not handled yet.");
  return null;
};

const Blog = ({ blogPost, relatedBlogPosts }) => {
  const {
    seo,
    type,
    publishedAt,
    title,
    description,
    image,
    author,
    blocks,
    search,
  } = blogPost.attributes;

  const formatDate = date => {
    return new Date(date).toLocaleDateString(undefined, {
      year: "numeric",
      month: "long",
    });
  };

  const renderAuthorImage = () => {
    const { url, alternativeText } =
      author?.data?.attributes?.avatar?.data?.attributes || {};
    return (
      <Image
        layout="fill"
        objectFit="contain"
        src={url}
        alt={alternativeText}
      />
    );
  };

  return (
    <div className="mb-[40px] flex flex-col gap-y-[24px] px-[16px] md:gap-y-[40px] xl:px-0">
      <SEO
        title={seo?.title ?? title}
        description={seo?.description ?? description}
        image={seo?.image}
      >
        <meta name="robots" content="max-image-preview:large" />
      </SEO>
      <FeedHead />
      <div className="px-4 py-4">
        <Breadcrumb
          items={[{ label: "Blog", href: "/blog" }, { label: title }]}
        />
      </div>
      <span className="flex flex-col items-center justify-center md:flex-row">
        <WhoppahFullSize className="text-[#E26F77]" />
        <span className="ml-[10px] text-[28px] leading-[28px] text-[#E26F77] md:text-[36px]">
          {type?.data?.attributes?.title}
        </span>
      </span>
      <section className="grid grid-cols-1 gap-x-[24px] gap-y-[16px] md:grid-cols-2">
        <div
          className="order-2 flex flex-col gap-y-[24px] rounded-[10px] px-[16px] py-[24px] md:order-1 md:px-[48px] md:py-[100px]"
          style={{
            backgroundColor:
              type?.data?.attributes?.backgroundColor ?? "#FEF7DF",
          }}
        >
          <h1 className="font-poppins text-[24px] font-semibold md:text-[36px]">
            {title}
          </h1>
          <p className="text-[16px] md:text-[18px]">{description}</p>
          <div className="flex items-center">
            <div className="relative h-[40px] w-[40px] rounded-full md:h-[80px] md:w-[80px]">
              {renderAuthorImage()}
            </div>
            <div className="ml-[10px] flex flex-col">
              <span
                className="font-poppins text-[16px] font-semibold md:text-[18px]"
                style={{
                  color: type?.data?.attributes?.foregroundColor ?? "#E26F77",
                }}
              >
                {author?.data?.attributes?.name}
              </span>
              <span>{formatDate(publishedAt)}</span>
            </div>
          </div>
        </div>
        <div className="relative order-1 w-full overflow-hidden rounded-[10px] md:order-2">
          <Image
            layout="fill"
            objectFit="cover"
            priority
            src={image?.data?.attributes?.url}
            alt={image?.data?.attributes?.alternativeText || ""}
          />
        </div>
      </section>
      {blocks.map(block => (
        <Block key={block.id} block={block} author={author} />
      ))}
      {search ? <BlogProducts search={search} /> : null}
      {relatedBlogPosts?.length > 0 ? (
        <OtherBlogs blogs={relatedBlogPosts} />
      ) : null}
    </div>
  );
};

export default Blog;
